import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { QrCode, Printer, Loader2, Copy, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { openPrintWindow } from '@/utils/printWindow';
import API from '@/config/api';

/**
 * Admin/provider component that renders a printable QR-code poster for parent
 * self check-in. Scanning the QR opens /parent/checkin in the parent's
 * browser; if they're not logged in, the app prompts them to log in first.
 *
 * Print the poster and tape it to the front desk.
 */
const ParentCheckinQRPoster = () => {
  const { getAuthHeaders } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Pass the current site's origin so the QR encodes the right URL
        // even when the proxy rewrites Origin/Referer headers.
        const origin = encodeURIComponent(window.location.origin);
        const res = await axios.get(`${API}/parent/checkin/qr-info?origin=${origin}`, getAuthHeaders());
        setInfo(res.data);
      } catch {
        toast.error('Failed to generate QR code');
      } finally {
        setLoading(false);
      }
    })();
  }, [getAuthHeaders]);

  const copyUrl = async () => {
    if (!info?.target_url) return;
    try {
      await navigator.clipboard.writeText(info.target_url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  const printPoster = () => {
    if (!info?.data_uri) return;
    const html = `
      <!doctype html>
      <html><head><title>Parent Sign-In Poster</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; text-align: center; padding: 48px 32px; color: #0f172a; }
        h1 { font-size: 36px; margin: 0 0 8px; }
        h2 { font-size: 20px; margin: 0 0 32px; color: #64748b; font-weight: 500; }
        img { width: 360px; height: 360px; image-rendering: pixelated; border: 8px solid #0f766e; border-radius: 24px; padding: 16px; background: white; }
        ol { text-align: left; max-width: 460px; margin: 32px auto 0; font-size: 16px; line-height: 1.7; color: #334155; }
        .url { margin-top: 24px; font-size: 13px; color: #64748b; }
      </style>
      </head><body>
        <h1>Parent Sign In &amp; Sign Out</h1>
        <h2>Scan to check your child in or out</h2>
        <img src="${info.data_uri}" alt="Sign in QR code" />
        <ol>
          <li>Open your phone camera and point it at the QR code.</li>
          <li>Tap the link that appears.</li>
          <li>Log in to your CareKids parent account.</li>
          <li>Tap <strong>Sign In</strong> or <strong>Sign Out</strong> next to your child's name.</li>
        </ol>
        <p class="url">Or visit: ${info.target_url}</p>
      </body></html>
    `;
    const w = openPrintWindow(html);
    if (!w) toast.error('Please allow pop-ups to print');
  };

  return (
    <Card className="card-clay" data-testid="parent-checkin-qr-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <QrCode className="w-5 h-5 text-primary" />
          Parent Self Check-In QR Code
        </CardTitle>
        <CardDescription>
          Print this poster and tape it to the front desk. Parents scan it with their phone camera to sign their children in or out using their CareKids login.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : info ? (
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="bg-white border-4 border-primary/30 rounded-2xl p-4 shadow-sm">
              <img
                src={info.data_uri}
                alt="Parent check-in QR code"
                className="w-48 h-48"
                style={{ imageRendering: 'pixelated' }}
                data-testid="parent-qr-image"
              />
            </div>
            <div className="flex-1 space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Scanning the QR opens:</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="font-mono text-xs bg-muted px-2 py-1 rounded break-all flex-1" data-testid="parent-qr-url">
                    {info.target_url}
                  </code>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={copyUrl}
                    className="h-7 w-7 flex-shrink-0"
                    data-testid="parent-qr-copy"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Parents must be logged in (or will be prompted to log in) and only see children linked to their account.
              </p>
              <Button onClick={printPoster} className="w-full md:w-auto" data-testid="parent-qr-print">
                <Printer className="w-4 h-4 mr-2" /> Print Sign-In Poster
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ParentCheckinQRPoster;
